import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { ADMIN_CREATE_USER } from "../../Users/api/UsersQuery";
import {
  CheckCircle2,
  Crown,
  Download,
  Eye,
  EyeOff,
  PiggyBank,
  RefreshCw,
  Search,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BuyAdminManagedUser,
  BuyAdminUserAdminSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./UserAdminManagement.module.css";
import MemberSalesRep from "./MemberSalesRep";

const PAGE_SIZE = 50;
const FILTER_FETCH_SIZE = 200;
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const emptyCreateMemberForm = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  referralCode: "",
  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  password: "",
  confirmPassword: "",
};

type CreateMemberField = keyof typeof emptyCreateMemberForm;

type SortColumn = "name" | "tickets" | "spent" | "savings" | "joined";
type SortDir = "asc" | "desc";
type MemberStatus = "comp" | "not_verified" | "verified" | "free" | "annual" | "monthly";
type MemberFilter = "" | MemberStatus;

const emptySummary: BuyAdminUserAdminSummary = {};
const memberFilterOptions: Array<{ label: string; value: MemberFilter }> = [
  { label: "All Members", value: "" },
  { label: "Not Verified", value: "not_verified" },
  { label: "Verified", value: "verified" },
  { label: "Free", value: "free" },
  { label: "Annual", value: "annual" },
  { label: "Monthly", value: "monthly" },
];

const getActor = () => ({
  changedBy: localStorage.getItem("qa-admin-user") || "",
  role: localStorage.getItem("x-hasura-user-role") || "",
});

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isMemberDeactivated = (user: BuyAdminManagedUser) =>
  Boolean(user.is_deactivated ?? !user.is_active);

const formatMembershipEnd = (value?: string | null) => value
  ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
  : "-";

/** Same rule Sell Add User uses to fall back to /api/staffCreateUser. */
const shouldFallbackToNodeApiCreate = (error: any): boolean => {
  const graphQlMessage = error?.graphQLErrors?.[0]?.message || "";
  const message = (error?.message || graphQlMessage || "").toLowerCase();
  return (
    message.includes("cannot query field 'admincreateuser'") ||
    message.includes("field 'admincreateuser' not found") ||
    message.includes('unknown type "admincreateuserinput"') ||
    message.includes("is not defined by type 'admincreateuserinput'") ||
    message.includes('is not defined by type "admincreateuserinput"') ||
    message.includes('argument "input" has invalid value') ||
    message.includes("query is not allowed") ||
    message.includes("mutation is not allowed") ||
    message.includes("allowlist")
  );
};

const normalizeStatus = (user: BuyAdminManagedUser): MemberStatus => {
  // Comp: dashboard-created members until an admin ends it, and extended members
  // until their extension date. Computed live by the backend.
  if (user.has_live_admin_grant) return "comp";
  if (!user.is_verified) return "not_verified";
  const membershipStatus = String(user.membership_status || "").trim().toLowerCase();
  const subscriptionType = String(user.subscription_type || "").trim().toLowerCase();
  const membershipEnd = user.membership_end_at ? new Date(user.membership_end_at).getTime() : Number.NaN;
  const hasCurrentPaidMembership = membershipStatus === "premium"
    || (Number.isFinite(membershipEnd) && membershipEnd >= Date.now());
  if (hasCurrentPaidMembership && subscriptionType === "annual") return "annual";
  if (hasCurrentPaidMembership && subscriptionType === "monthly") return "monthly";
  // Former Comp members show as Free until they buy a plan.
  if (membershipStatus === "free" || user.had_admin_grant) return "free";
  return "verified";
};

const statusLabel = (status: string) =>
  status
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getSortValue = (row: BuyAdminManagedUser, column: SortColumn) => {
  switch (column) {
    case "tickets":
      return row.order_count;
    case "spent":
      return row.lifetime_spend;
    case "savings":
      return row.lifetime_savings;
    case "joined":
      return row.created_at ? new Date(row.created_at).getTime() : 0;
    case "name":
    default:
      return row.full_name || row.email;
  }
};

const UserAdminManagement = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<BuyAdminManagedUser[]>([]);
  const [summary, setSummary] = useState<BuyAdminUserAdminSummary>(emptySummary);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [memberFilter, setMemberFilter] = useState<MemberFilter>("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("joined");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [loading, setLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [extensionMember, setExtensionMember] = useState<BuyAdminManagedUser | null>(null);
  const [extensionDate, setExtensionDate] = useState("");
  const [extensionError, setExtensionError] = useState("");
  const [extending, setExtending] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [createMemberForm, setCreateMemberForm] = useState(emptyCreateMemberForm);
  const [createMemberErrors, setCreateMemberErrors] = useState<Partial<Record<CreateMemberField, string>>>({});
  const [createMemberError, setCreateMemberError] = useState("");
  const [creatingMember, setCreatingMember] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  // Sell Add User's create action: one account on both sides, like a website signup.
  const [adminCreateSellUser] = useMutation(ADMIN_CREATE_USER);

  const closeAddMember = () => {
    if (creatingMember) return;
    setShowAddMember(false);
    setCreateMemberForm(emptyCreateMemberForm);
    setCreateMemberErrors({});
    setCreateMemberError("");
    setShowCreatePassword(false);
  };

  const updateCreateMemberField = (field: CreateMemberField, value: string) => {
    setCreateMemberForm((current) => ({ ...current, [field]: value }));
    setCreateMemberErrors((current) => ({ ...current, [field]: undefined }));
  };

  const createMember = async (event: React.FormEvent) => {
    event.preventDefault();
    if (creatingMember) return;

    const values = {
      ...createMemberForm,
      firstName: createMemberForm.firstName.trim(),
      lastName: createMemberForm.lastName.trim(),
      email: createMemberForm.email.trim().toLowerCase(),
      phoneNumber: createMemberForm.phoneNumber.replace(/\D/g, ""),
      streetAddress: createMemberForm.streetAddress.trim(),
      city: createMemberForm.city.trim(),
      zipCode: createMemberForm.zipCode.trim(),
      referralCode: createMemberForm.referralCode.trim(),
    };
    const errors: Partial<Record<CreateMemberField, string>> = {};
    if (!values.firstName) errors.firstName = "First name is required.";
    if (!values.lastName) errors.lastName = "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = "Enter a valid email address.";
    if (values.phoneNumber.length !== 10) errors.phoneNumber = "Phone number must be exactly 10 digits.";
    if (!values.streetAddress) errors.streetAddress = "Street address is required.";
    if (!values.city) errors.city = "City is required.";
    if (!values.state) errors.state = "State is required.";
    if (!/^\d{5}$/.test(values.zipCode)) errors.zipCode = "ZIP code must be 5 digits.";
    if (values.password.length < 10 || !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(values.password)) {
      errors.password = "Use at least 10 characters and one special character.";
    }
    if (values.confirmPassword !== values.password) errors.confirmPassword = "Passwords do not match.";

    if (Object.keys(errors).length > 0) {
      setCreateMemberErrors(errors);
      return;
    }

    setCreatingMember(true);
    setCreateMemberError("");
    setErrorMessage("");
    setSuccessMessage("");
    const memberInput = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      streetAddress: values.streetAddress,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
      password: values.password,
      referralCode: values.referralCode || undefined,
    };
    let sellUserId: string | null = null;
    try {
      // 1. Check everything first, so a sell account is only created when the buy record will save.
      await buyAdminApi.createBuyAdminMember({ ...memberInput, validateOnly: true });

      // 2. Create the sell account exactly as Sell Add User does, with its defaults.
      const sellInput = {
        firstName: values.firstName,
        lastName: values.lastName,
        emailId: values.email,
        phoneNumber: values.phoneNumber,
        countryCode: "+1",
        clientType: "concierge",
        password: values.password,
        percentage: 20,
        businessName: null,
        salesRepId: null,
        affiliateId: null,
        teams: [] as string[],
      };
      try {
        const sellResponse = await adminCreateSellUser({ variables: { input: sellInput } });
        sellUserId = sellResponse?.data?.adminCreateUser?.user?.u_id || null;
      } catch (sellError: any) {
        if (!shouldFallbackToNodeApiCreate(sellError)) throw sellError;
        const nodeResponse = await buyAdminApi.createSellUserViaNodeApi(sellInput);
        sellUserId = nodeResponse?.user?.u_id || null;
      }
      if (!sellUserId) throw new Error("The sell account was not created. Nothing was saved.");

      // 3. Save the buy record linked to that sell account, marked as Comp.
      const response = await buyAdminApi.createBuyAdminMember({ ...memberInput, sellUserId });
      setShowAddMember(false);
      setCreateMemberForm(emptyCreateMemberForm);
      setCreateMemberErrors({});
      setCreateMemberError("");
      setShowCreatePassword(false);
      setSearch("");
      setSubmittedSearch("");
      setMemberFilter("");
      setPage(0);
      setSuccessMessage(response.message || "Member created successfully.");
      await loadMembers();
    } catch (error: any) {
      const rawMessage = error?.message || error?.graphQLErrors?.[0]?.message || "";
      const normalized = rawMessage.toLowerCase();
      const isDuplicate = normalized.includes("user_u_email_id_key")
        || normalized.includes("duplicate key value violates unique constraint")
        || normalized.includes("user already exists")
        || normalized.includes("already in use")
        || normalized.includes("already exists");
      const message = isDuplicate
        ? "User already exists with this email."
        : sellUserId
          ? `The sell account was created, but the buy member was not saved: ${rawMessage || "unknown error"}`
          : rawMessage || "Failed to create member.";
      setCreateMemberErrors({ email: isDuplicate ? message : undefined });
      setCreateMemberError(message);
    } finally {
      setCreatingMember(false);
    }
  };

  const saveExtension = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!extensionMember || extending) return;
    setExtending(true);
    setExtensionError("");
    try {
      await buyAdminApi.extendBuyAdminMembership(extensionMember.id, {
        endDate: extensionDate, expectedEndDate: extensionMember.membership_end_at || null,
      });
      setExtensionMember(null);
      setSuccessMessage("Membership extended. A $0.00 Admin Extension was added to history.");
      await loadMembers();
    } catch (error: any) {
      setExtensionError(error?.message || "Failed to extend membership.");
    } finally { setExtending(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const offset = page * PAGE_SIZE;

  const pageSavings = useMemo(
    () => members.reduce((sum, member) => sum + Number(member.lifetime_savings || 0), 0),
    [members],
  );

  const inactiveCount = useMemo(
    () => members.filter((member) => isMemberDeactivated(member)).length,
    [members],
  );

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const valueA = getSortValue(a, sortColumn);
      const valueB = getSortValue(b, sortColumn);

      if (typeof valueA === "string" || typeof valueB === "string") {
        const comparison = String(valueA).localeCompare(String(valueB));
        return sortDir === "asc" ? comparison : -comparison;
      }

      const comparison = Number(valueA) - Number(valueB);
      return sortDir === "asc" ? comparison : -comparison;
    });
  }, [members, sortColumn, sortDir]);

  const loadMembers = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      if (memberFilter) {
        const collected: BuyAdminManagedUser[] = [];
        let nextOffset = 0;
        let totalAvailable = 0;

        do {
          const response = await buyAdminApi.getUserAdminCustomers({
            limit: FILTER_FETCH_SIZE,
            offset: nextOffset,
            search: submittedSearch.trim(),
          });
          collected.push(...response.data);
          totalAvailable = response.pagination.total;
          nextOffset += FILTER_FETCH_SIZE;
        } while (collected.length < totalAvailable);

        const filteredMembers = collected.filter((member) => normalizeStatus(member) === memberFilter);
        const pageStart = page * PAGE_SIZE;
        const pageRows = filteredMembers.slice(pageStart, pageStart + PAGE_SIZE);

        setMembers(pageRows);
        setSummary({
          customer_count: filteredMembers.length,
          premium_count: filteredMembers.filter((member) => ["annual", "monthly"].includes(normalizeStatus(member))).length,
          active_count: filteredMembers.filter((member) => !isMemberDeactivated(member) && Boolean(member.is_verified)).length,
        });
        setTotal(filteredMembers.length);
        return;
      }

      const response = await buyAdminApi.getUserAdminCustomers({
        limit: PAGE_SIZE,
        offset,
        search: submittedSearch.trim(),
      });
      setMembers(response.data);
      setSummary(response.summary || emptySummary);
      setTotal(response.pagination.total);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load members.");
      setMembers([]);
      setSummary(emptySummary);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, submittedSearch, memberFilter]);

  useEffect(() => {
    if (!errorMessage && !successMessage) return;
    const timer = window.setTimeout(() => {
      setErrorMessage("");
      setSuccessMessage("");
    }, 2400);
    return () => window.clearTimeout(timer);
  }, [errorMessage, successMessage]);

  const handleSearch = () => {
    setPage(0);
    setSubmittedSearch(search);
  };

  const clearSearch = () => {
    setSearch("");
    setSubmittedSearch("");
    setPage(0);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDir(column === "name" ? "asc" : "desc");
  };

  const exportCsv = () => {
    const headers = [
      "Member",
      "Email",
      "Phone",
      "Status",
      "Orders",
      "Spent",
      "Savings",
      "Joined",
      "Last Active",
      "Referral Code",
      "Sales Rep",
    ];
    const rows = sortedMembers.map((member) => [
      member.full_name,
      member.email,
      member.phone_number || "",
      statusLabel(normalizeStatus(member)),
      member.order_count,
      member.lifetime_spend,
      member.lifetime_savings,
      formatDate(member.created_at),
      formatDate(member.last_activity_at || member.last_login_at),
      member.referral_code || "",
      member.sales_rep_name || member.sales_rep_id || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `buy-members-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const updateUserStatus = async (user: BuyAdminManagedUser) => {
    setSavingUserId(user.id);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const deactivated = isMemberDeactivated(user);
      await buyAdminApi.updateBuyAdminUserStatus(user.id, {
        isActive: deactivated,
        note: deactivated ? "Activated from Buy Admin Members" : "Deactivated from Buy Admin Members",
        ...getActor(),
      });
      setSuccessMessage(deactivated ? "Member activated." : "Member deactivated.");
      await loadMembers();
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to update member status.");
    } finally {
      setSavingUserId("");
    }
  };

  const SortLabel = ({ column, children }: { column: SortColumn; children: React.ReactNode }) => (
    <button className={styles.sortButton} onClick={() => handleSort(column)}>
      {children}
      {sortColumn === column && <span>{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="buy-members-page">
      <div className={styles.header}>
        <div>
          <div className={styles.eyebrow}>Buy Admin</div>
          <div className={styles.titleRow}>
            <Users size={24} />
            <h1>Members</h1>
          </div>
          <p>Manage buy-side customer accounts from the buy backend database.</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadMembers}
            disabled={loading}
            aria-label="Refresh members"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={`${styles.metric} ${styles.metricTotal}`}>
          <div>
            <span>Total Members</span>
            <strong>{summary.customer_count || total}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconTotal}`}>
            <Users size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricActive}`}>
          <div>
            <span>Active Members</span>
            <strong>{summary.active_count || 0}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconActive}`}>
            <CheckCircle2 size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricPremium}`}>
          <div>
            <span>Premium</span>
            <strong>{summary.premium_count || 0}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconPremium}`}>
            <Crown size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricSavings}`}>
          <div>
            <span>Page Savings</span>
            <strong>{formatCurrency(pageSavings)}</strong>
            <small>{inactiveCount} inactive on this page</small>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconSavings}`}>
            <PiggyBank size={18} />
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSearch();
            }}
            placeholder="Search by member name, email, phone, or user ID..."
          />
          {search && (
            <button type="button" className={styles.searchClearButton} onClick={clearSearch} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </div>
        <button className={styles.primaryButton} onClick={handleSearch}>
          <Search size={16} />
          Search
        </button>
        <select
          value={memberFilter}
          onChange={(event) => {
            setMemberFilter(event.target.value as MemberFilter);
            setPage(0);
          }}
          aria-label="Member status"
        >
          {memberFilterOptions.map((option) => (
            <option key={option.label} value={option.value}>{option.label}</option>
          ))}
        </select>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => setShowAddMember(true)}
        >
          <UserPlus size={16} />
          Add User
        </button>
        <button className={styles.secondaryButton} onClick={exportCsv} disabled={members.length === 0}>
          <Download size={16} />
          Export CSV
        </button>
      </div>

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th><SortLabel column="name">Member</SortLabel></th>
              <th>Email</th>
              <th>Status</th>
              <th>Phone</th>
              <th>Sales Rep</th>
              <th>Subscription Membership End Date</th>
              <th className={styles.center}><SortLabel column="tickets">Tickets</SortLabel></th>
              <th className={styles.right}><SortLabel column="spent">Spent</SortLabel></th>
              <th className={styles.right}><SortLabel column="savings">Savings</SortLabel></th>
              <th><SortLabel column="joined">Joined</SortLabel></th>
              <th>Last Active</th>
              <th className={styles.center}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className={styles.emptyCell}>Loading members...</td>
              </tr>
            ) : sortedMembers.length === 0 ? (
              <tr>
                <td colSpan={12} className={styles.emptyCell}>No members found.</td>
              </tr>
            ) : (
              sortedMembers.map((member) => {
                const status = normalizeStatus(member);
                const isSaving = savingUserId === member.id;
                return (
                  <tr key={member.id} className={styles.row}>
                    <td>
                      <strong>{member.full_name || member.email}</strong>
                      <div className={styles.subText}>{member.id}</div>
                    </td>
                    <td className={styles.mutedCell}>{member.email}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[`status_${status}`] || styles.status_active}`}>
                        {statusLabel(status)}
                      </span>
                    </td>
                    <td>{member.phone_number || "-"}</td>
                    <td><MemberSalesRep member={member} disabled={isSaving || isMemberDeactivated(member)}
                      onAssigned={(assignment) => {
                        setMembers((current) => current.map((item) => item.id === member.id ? { ...item, ...assignment } : item));
                        setErrorMessage("");
                        setSuccessMessage(assignment.sales_rep_id ? "Sales representative assigned." : "Sales representative removed.");
                      }} /></td>
                    <td>
                      <div>{formatMembershipEnd(member.membership_end_at)}</div>
                      {member.can_extend_membership ? (
                        <button className={styles.secondaryButton} onClick={() => {
                          setExtensionMember(member); setExtensionDate(""); setExtensionError("");
                        }}>Extend Membership</button>
                      ) : null}
                    </td>
                    <td className={styles.center}>{member.order_count}</td>
                    <td className={styles.right}>{formatCurrency(member.lifetime_spend)}</td>
                    <td className={`${styles.right} ${styles.savingsText}`}>{formatCurrency(member.lifetime_savings)}</td>
                    <td>{formatDate(member.created_at)}</td>
                    <td>{formatDate(member.last_activity_at || member.last_login_at)}</td>
                    <td className={styles.center}>
                      <div className={styles.actions}>
                        <button
                          className={styles.iconButton}
                          title="View personal info"
                          onClick={() =>
                            navigate(`/buy-user-admin-management/${member.id}`, {
                              state: { member },
                            })
                          }
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className={!isMemberDeactivated(member) ? styles.warningButton : styles.activateButton}
                          onClick={() => updateUserStatus(member)}
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : !isMemberDeactivated(member) ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <div className={styles.tableFooter}>
          <span>
            Showing {members.length === 0 ? 0 : offset + 1}-{offset + members.length} of {total} members
          </span>
          <div className={styles.pagination}>
            <button disabled={page === 0 || loading} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>
              Previous
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button disabled={page + 1 >= totalPages || loading} onClick={() => setPage((prev) => prev + 1)}>
              Next
            </button>
          </div>
        </div>
      </div>

      {showAddMember && (
        <div className={styles.modalOverlay} onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeAddMember();
        }}>
          <form className={styles.memberModal} onSubmit={createMember} role="dialog" aria-modal="true" aria-labelledby="add-member-title">
            <div className={styles.modalHeader}>
              <div>
                <h2 id="add-member-title">Add New User</h2>
                <p>Creates a Buy account in Comp status. No phone verification code is required.</p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeAddMember} disabled={creatingMember} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {createMemberError && <div className={styles.formAlert} role="alert">{createMemberError}</div>}
              <div className={styles.formGrid}>
                <label className={styles.formField}>
                  <span>First Name *</span>
                  <input autoFocus value={createMemberForm.firstName} onChange={(event) => updateCreateMemberField("firstName", event.target.value)} disabled={creatingMember} />
                  {createMemberErrors.firstName && <small>{createMemberErrors.firstName}</small>}
                </label>
                <label className={styles.formField}>
                  <span>Last Name *</span>
                  <input value={createMemberForm.lastName} onChange={(event) => updateCreateMemberField("lastName", event.target.value)} disabled={creatingMember} />
                  {createMemberErrors.lastName && <small>{createMemberErrors.lastName}</small>}
                </label>
                <label className={styles.formField}>
                  <span>Email *</span>
                  <input type="email" value={createMemberForm.email} onChange={(event) => updateCreateMemberField("email", event.target.value)} placeholder="user@example.com" disabled={creatingMember} />
                  {createMemberErrors.email && <small>{createMemberErrors.email}</small>}
                </label>
                <label className={styles.formField}>
                  <span>Phone Number *</span>
                  <div className={styles.phoneField}>
                    <span>+1</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={createMemberForm.phoneNumber}
                      onChange={(event) => updateCreateMemberField("phoneNumber", event.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="9529564675"
                      disabled={creatingMember}
                    />
                  </div>
                  {createMemberErrors.phoneNumber && <small>{createMemberErrors.phoneNumber}</small>}
                </label>
                <label className={`${styles.formField} ${styles.formFieldWide}`}>
                  <span>Referral Code</span>
                  <input value={createMemberForm.referralCode} onChange={(event) => updateCreateMemberField("referralCode", event.target.value)} placeholder="Optional" disabled={creatingMember} />
                  {createMemberErrors.referralCode && <small>{createMemberErrors.referralCode}</small>}
                </label>
                <label className={`${styles.formField} ${styles.formFieldWide}`}>
                  <span>Street Address *</span>
                  <input value={createMemberForm.streetAddress} onChange={(event) => updateCreateMemberField("streetAddress", event.target.value)} placeholder="123 Main Street" disabled={creatingMember} />
                  {createMemberErrors.streetAddress && <small>{createMemberErrors.streetAddress}</small>}
                </label>
                <label className={styles.formField}>
                  <span>City *</span>
                  <input value={createMemberForm.city} onChange={(event) => updateCreateMemberField("city", event.target.value)} disabled={creatingMember} />
                  {createMemberErrors.city && <small>{createMemberErrors.city}</small>}
                </label>
                <label className={styles.formField}>
                  <span>State *</span>
                  <select value={createMemberForm.state} onChange={(event) => updateCreateMemberField("state", event.target.value)} disabled={creatingMember}>
                    <option value="">Select</option>
                    {US_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
                  </select>
                  {createMemberErrors.state && <small>{createMemberErrors.state}</small>}
                </label>
                <label className={styles.formField}>
                  <span>ZIP Code *</span>
                  <input
                    inputMode="numeric"
                    value={createMemberForm.zipCode}
                    onChange={(event) => updateCreateMemberField("zipCode", event.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="10001"
                    disabled={creatingMember}
                  />
                  {createMemberErrors.zipCode && <small>{createMemberErrors.zipCode}</small>}
                </label>
                <div aria-hidden="true" />
                <label className={styles.formField}>
                  <span>Password *</span>
                  <div className={styles.passwordField}>
                    <input
                      type={showCreatePassword ? "text" : "password"}
                      value={createMemberForm.password}
                      onChange={(event) => updateCreateMemberField("password", event.target.value)}
                      autoComplete="new-password"
                      disabled={creatingMember}
                    />
                    <button type="button" onClick={() => setShowCreatePassword((current) => !current)} tabIndex={-1} aria-label={showCreatePassword ? "Hide password" : "Show password"}>
                      {showCreatePassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {createMemberErrors.password && <small>{createMemberErrors.password}</small>}
                </label>
                <label className={styles.formField}>
                  <span>Confirm Password *</span>
                  <input
                    type={showCreatePassword ? "text" : "password"}
                    value={createMemberForm.confirmPassword}
                    onChange={(event) => updateCreateMemberField("confirmPassword", event.target.value)}
                    autoComplete="new-password"
                    disabled={creatingMember}
                  />
                  {createMemberErrors.confirmPassword && <small>{createMemberErrors.confirmPassword}</small>}
                </label>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <span>The user can sign in immediately; Admin status starts as Comp.</span>
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={closeAddMember} disabled={creatingMember}>Cancel</button>
                <button type="submit" className={styles.primaryButton} disabled={creatingMember}>
                  {creatingMember ? "Creating..." : "Create User"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {extensionMember && (
        <div className={styles.alertOverlay}>
          <form className={styles.alertModal} onSubmit={saveExtension} role="dialog" aria-modal="true" aria-labelledby="extend-membership-title">
            <h2 id="extend-membership-title">Extend membership</h2>
            <p>{extensionMember.full_name || extensionMember.email}</p>
            <p>Current end date: {formatMembershipEnd(extensionMember.membership_end_at)}</p>
            <label htmlFor="membership-extension-date">New end date (through the end of the day, UTC)</label>
            <input id="membership-extension-date" type="date" required autoFocus value={extensionDate}
              min={[new Date().toISOString().slice(0, 10), extensionMember.membership_end_at?.slice(0, 10) || ''].sort().pop()} disabled={extending}
              onChange={(event) => setExtensionDate(event.target.value)} />
            <p>This adds a $0.00 Admin Extension to subscription history.</p>
            {(!extensionMember.membership_end_at || new Date(extensionMember.membership_end_at).getTime() <= Date.now()) &&
              <p>The membership period starts today.</p>}
            {isMemberDeactivated(extensionMember) && <p>This account remains deactivated until activated separately.</p>}
            {extensionError && <p role="alert">{extensionError}</p>}
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryButton} disabled={extending} onClick={() => setExtensionMember(null)}>Cancel</button>
              <button type="submit" className={styles.primaryButton} disabled={extending || !extensionDate}>
                {extending ? "Saving..." : "Save extension"}
              </button>
            </div>
          </form>
        </div>
      )}
      {(errorMessage || successMessage) && (
        <div className={styles.alertOverlay}>
          <div className={`${styles.alertModal} ${errorMessage ? styles.alertError : styles.alertSuccess}`}>
            <span>
              {errorMessage ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
              {errorMessage || successMessage}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdminManagement;
